import { Navigate, Route, Routes } from "react-router";

import NotFoundPage from "components/error/NotFoundPage";
import useRefreshedUsagePageFlag from "shared/Usage/useRefreshedPageFlag";
import useIsFlowsIntegrationEnabled from "hooks/useIsFlowsIntegrationEnabled";
import { isSaasDistribution, isSelfHostedDistribution } from "utils/distribution";
import { isFedRampRegion } from "utils/region";
import useIsOnlySpaceAdmin from "shared/Space/useIsOnlySpaceAdmin";
import useAacFlags from "hooks/useAacFlags";

import AccountDeletion from "./AccountDeletion";
import AISettings from "./AI";
import ApiKeysV2 from "./ApiKeysV2";
import Billing from "./Billing";
import FlowsSettings from "./Flows";
import IdpGroupMappingV2 from "./IdpGroupMappingV2";
import Limits from "./Limits";
import LoginPolicies from "./LoginPolicies";
import ManagementStrategy from "./ManagementStrategy";
import ManageMFA from "./ManageMFA";
import ApiKeyCreate from "./ApiKeysV2/CreateFullScreenModal";
import RoleCreate from "./Role/Create";
import Roles from "./Roles";
import SecuritySettings from "./SecuritySettings";
import SelfHostedUsageExport from "./SelfHostedUsageExport";
import Sessions from "./Sessions";
import SettingsSingleSignOn from "./SingleSignOn";
import OidcSubjectTemplate from "./OidcSubjectTemplate";
import OauthRedirectAllowlist from "./OauthRedirectAllowlist";
import RuntimeSecurity from "./RuntimeSecurity";
import Spaces from "./Spaces";
import Usage from "./Usage";
import Users from "./Users";

const isSaas = isSaasDistribution();
const isSelfHosted = isSelfHostedDistribution();
const isFedRamp = isFedRampRegion();

type SettingsRoutesProps = {
  securityEmail?: string;
};

const SettingsRoutes = (props: SettingsRoutesProps) => {
  const isFlowsIntegrationEnabled = useIsFlowsIntegrationEnabled();
  const isRefreshedUsagePageEnabled = useRefreshedUsagePageFlag();
  const { securityEmail } = props;

  const { nonRootAdminRoleBindings } = useAacFlags();
  const isOnlySpaceAdmin = useIsOnlySpaceAdmin();

  if (isOnlySpaceAdmin) {
    return (
      <Routes>
        <Route index element={<Navigate to="roles" replace />} />
        <Route path="roles" element={<Roles readOnly />} />
        {nonRootAdminRoleBindings && (
          <>
            <Route path="users" element={<Users />} />
            <Route path="idp-group-mapping" element={<IdpGroupMappingV2 />} />
            <Route path="api-keys" element={<ApiKeysV2 />} />
          </>
        )}
        <Route path="*" element={<NotFoundPage />} />
      </Routes>
    );
  }

  return (
    <Routes>
      <Route index element={<Navigate to="management-strategy" replace />} />

      <Route path="sso">
        <Route index path="*" element={<SettingsSingleSignOn />} />
      </Route>

      <Route path="mfa" element={<ManageMFA />} />
      <Route path="slack" element={<Navigate to="/settings/integrations" replace />} />
      <Route path="api-keys" element={<ApiKeysV2 />} />
      <Route path="spaces" element={<Spaces />} />
      <Route path="login-policy" element={<LoginPolicies />} />
      <Route path="audit-trail" element={<Navigate to="/audit-trail" replace />} />
      {isSaas && <Route path="limits" element={<Limits />} />}
      {isSaas && !isFedRamp && (
        <Route path="billing">
          <Route index path="*" element={<Billing />} />
        </Route>
      )}
      {(isSaas || isRefreshedUsagePageEnabled) && (
        <Route path="usage">
          <Route index path="*" element={<Usage />} />
        </Route>
      )}

      {isSaas && typeof securityEmail !== "undefined" && (
        <Route path="security" element={<SecuritySettings securityEmail={securityEmail} />} />
      )}
      <Route path="runtime-security" element={<RuntimeSecurity />} />
      <Route path="oidc-subject-template" element={<OidcSubjectTemplate />} />
      <Route path="oauth-redirect-allowlist" element={<OauthRedirectAllowlist />} />
      <Route path="sessions" element={<Sessions />} />
      <Route path="users" element={<Users />} />
      <Route path="idp-group-mapping" element={<IdpGroupMappingV2 />} />
      <Route path="management-strategy" element={<ManagementStrategy />} />

      <Route path="roles" element={<Roles />} />
      <Route path="new/role" element={<RoleCreate />} />
      <Route path="new/api-key" element={<ApiKeyCreate />} />
      <Route path="integrations" element={<Navigate to="/integrations" replace />} />
      <Route path="ai" element={<AISettings />} />
      {isFlowsIntegrationEnabled && <Route path="flows" element={<FlowsSettings />} />}
      {isSelfHosted && <Route path="usage-export" element={<SelfHostedUsageExport />} />}
      {isSaas && <Route path="account-deletion" element={<AccountDeletion />} />}
      <Route path="*" element={<NotFoundPage />} />
    </Routes>
  );
};

export default SettingsRoutes;
